import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addSpreadFlyers } from '../../redux/advertSlice';
import { getContrastingColor, getLocalUTCDate, getStartOfMonthTimestamp, getStorageData } from '../../helpers';
import { Select } from '../../components/Select';
import { ADVERT_CITY_STORAGE_NAME, createHandlerWithStorage } from '../../helpers/localStorage';
import { AdvertMonthSwitcher } from './AdvertMonthSwitcher';
import { Button } from '../../components/Button';
import { Popup } from '../../components/Popup';
import { Input } from '../../components/Input';
import { SearchSelect } from '../../components/SearchSelect';
import sortImg from '../../assets/sort/sort.png';
import sortDarkImg from '../../assets/sort/sort-dark.png';

function getGroupList(list, groupType) {
    if (groupType === 'null') {
        return list;
    }

    const m = new Map();
    list.forEach(item => {
        const groupBy = groupType === 'promoter' ? item.promoter._id : new Date(item.date).toDateString();
        if (!m.has(groupBy)) {
            m.set(groupBy, item);
        } else {
            const flyersCount = m.get(groupBy).flyersCount;
            m.set(groupBy, {...m.get(groupBy), flyersCount: flyersCount + item.flyersCount});
        }
    })
    return Array.from(m.values()).sort((a,b) => groupType === 'promoter' ? b.flyersCount - a.flyersCount : b.date - a.date);
}

function formatAdditionalSpreadFlyers(list, flyerTypes, routes, promoters, cities) {
    return list.map(sf => {
        const prom = sf.promoter ? promoters.find(p => p._id === sf.promoter) : null;
        const promObject = prom ? {_id: prom._id, id: prom.id, fullname: prom.fullname} : {_id: 'null', id: 0, fullname: '—'};

        const flyerType = flyerTypes.find(ft => ft._id === sf.flyerType);

        const route = sf.route ? routes.find(r => r._id === sf.route) : null;
        const routeObject = route ? {_id: route._id, id: route.id} : {_id: 'null', id: 0};

        const city = cities.find(c => c._id === sf.city);

        return {
            ...sf,
            promoter: promObject,
            route: routeObject,
            flyerType,
            city,
        }
    })
}

const ResultItem = ({promoter, groupType, city, flyersCount, date, flyerType, route}) => {
    const flyerBgc = flyerType.details.backgroundColor;
    const flyerColor = getContrastingColor(flyerBgc);

    const isGrouped = groupType !== 'null';
    const isPromGroup = groupType === 'promoter';
    const isDateGroup = !isPromGroup;

    return (
        <div className="spread-flyers__item">
            {(!isGrouped || isDateGroup) && <div>{getLocalUTCDate(city.UTC, date)}</div>}
            {(isGrouped && !isDateGroup) && <div>—</div>}

            {(!isGrouped || isPromGroup) && <div>{promoter.id ? `(id${promoter.id}) `:''}{promoter.fullname}</div>}
            {(isGrouped && !isPromGroup) && <div>—</div>}
            
            <div className='spread-flyers__item-flyer'>
                {!isGrouped && <span style={{backgroundColor: flyerBgc, color: flyerColor}}>{flyerType.name}</span>}
                {isGrouped && <span>—</span>}
            </div>
            <div>{!isGrouped ? (route?.id || '—') : '—'}</div>
            <div>{flyersCount}</div>
        </div>
    );
};

function parseItemsFromActiveRoutes(promoters, mainUTCStr) {
    const list = [];
    promoters.forEach((prom) => {
        const { isFullReport } = prom;

        const thisPromRoutes = [prom.currentRoute, ...prom.routeQueue];
        thisPromRoutes.forEach(curRoute => {
            const m = new Map();
            curRoute.progress.forEach((h) => {
                const { porchCount, mailboxCount } = h;
                if (isFullReport) {
                    const fullfilledPorches = h.donePorches.filter(p => p.photoUrls.length >= 2 && p.videoUrl.name && !p.rejectComment); 
                    fullfilledPorches.forEach((p, _) => {
                        const d = new Date(p.photoUrls[0].time);
                        d.setHours(0, 0, 0, 0);
                        const dayStamp = +d;
                        const flyersCount = mailboxCount/porchCount;
                        if (!m.has(dayStamp)) {
                            m.set(dayStamp, flyersCount);
                        } else {
                            m.set(dayStamp, m.get(dayStamp) + flyersCount);
                        }
                    })
                } else {
                    const donePorches = h.donePorchUrls.filter(p => !p.rejectComment);
                    donePorches.forEach((photo) => {
                        const d = new Date(photo.time);
                        d.setHours(0, 0, 0, 0);
                        const dayStamp = +d;
                        const flyersCount = mailboxCount/porchCount;
                        if (!m.has(dayStamp)) {
                             m.set(dayStamp, flyersCount);
                        } else {
                             m.set(dayStamp, m.get(dayStamp) + flyersCount);
                        }
                    });
                }
            });
            m.forEach((value, key) => {
                const {_id, fullname, id} = prom;
                for (let i = 0; i < curRoute.flyers.length; i++) {
                    const f = curRoute.flyers[i];
                    if (!f.isOwn) continue;
                    list.push({
                        finishId: 'null',
                        promoter: {_id, fullname, id},
                        city: prom.city,
                        flyersCount: Math.round(value),
                        flyerType: f,
                        route: {
                            _id: curRoute._id,
                            id: curRoute.id,
                        },
                        date: key,
                        timestamp: getStartOfMonthTimestamp(new Date(key), mainUTCStr)
                    });
                }
            });
        })
    });

    return list;
}

export const SpreadFlyers = ({ cities, promoters, months, flyerTypes, routes }) => {
    const dispatch = useDispatch();
    const mainUTCStr = useSelector((state) => state.settings.mainUTC);
    const spreadFlyersList = useSelector((state) => state.advert.spreadFlyers);
    const additionalSpreadFlyers = useSelector((state) => state.advert.additionalSpreadFlyers);

    const [city, setCity] = useState(getStorageData(cities, ADVERT_CITY_STORAGE_NAME));
    const setCityHandler = createHandlerWithStorage(setCity, ADVERT_CITY_STORAGE_NAME);

    const activeProms = promoters.filter((p) => p.currentRoute._id && p.city._id === city);
    const activeRoutes = parseItemsFromActiveRoutes(activeProms, mainUTCStr);

    const formattedAdditionalSpreadFlyers = formatAdditionalSpreadFlyers(additionalSpreadFlyers, flyerTypes, routes, promoters, cities);
    const thisSpreadFlyers = [...spreadFlyersList, ...formattedAdditionalSpreadFlyers].filter(f => f.city._id === city);
    const activeMonth = months.find(m => m.active);
    const isLastMonth = activeMonth.timestamp === months[months.length-1].timestamp;
    const thisMonthSpreadFlyers = thisSpreadFlyers.filter(sf => sf.timestamp === activeMonth.timestamp);

    const itemsList = [...thisMonthSpreadFlyers, ...(isLastMonth ? activeRoutes : [])].sort((a,b) => b.date - a.date)
    
    const thisCityPromoters = [{_id: 'null', fullname: 'Все промоутеры'}, ...promoters.filter(p => p.city._id === city)];
    const [selectedProm, selectProm] = useState(thisCityPromoters[0]._id);

    const groupFilter = [{_id: 'null', name: 'Не группировать'}, {_id: 'promoter', name: 'Группировать по промам'}, {_id: 'date', name: 'Группировать по датам'}]
    const [groupType, setGroupType] = useState(groupFilter[0]._id);

    const promFilteredList = itemsList.filter(i => selectedProm === 'null' ? true : i.promoter._id === selectedProm)

    const groupList = getGroupList(promFilteredList, groupType);
    
    let minDate = Math.min(...itemsList.map(i => i.date));
    let maxDate = Math.max(...itemsList.map(i => i.date));

    useEffect(() => {
        const curMinDate = Math.min(...itemsList.map(i => i.date));
        const curMaxDate =  Math.max(...itemsList.map(i => i.date));
        setMinDay(new Date(new Date(curMinDate).setDate(1)).toLocaleDateString().split('.').reverse().join('-'))
        setMaxDay(new Date(new Date(new Date(curMaxDate).setDate(new Date(curMaxDate).getDate()+1)).setHours(0,0,0,0)).toLocaleDateString().split('.').reverse().join('-'))
    }, [activeMonth])

    const [minDay, setMinDay] = useState(new Date(new Date(minDate).setDate(1)).toLocaleDateString().split('.').reverse().join('-'))
    const [maxDay, setMaxDay] = useState(new Date(new Date(new Date(maxDate).setDate(new Date(maxDate).getDate()+1)).setHours(0,0,0,0)).toLocaleDateString().split('.').reverse().join('-'))

    const displayedList = groupList.filter(item => {

        return item.date >= new Date(new Date(minDay)).setHours(0,0,0,0) && item.date <= new Date(new Date(maxDay)).setHours(23,59,59,99)
    })

    const totalSpreadCount = displayedList.reduce((total, cur) => total + cur.flyersCount, 0)
    const fcountMap = new Map();
    displayedList.forEach(sc => {
        try {
            const thisFlyerCount = fcountMap.get(sc.flyerType.name);
            if (thisFlyerCount) {
                fcountMap.set(sc.flyerType.name, thisFlyerCount + sc.flyersCount)
            } else {
                fcountMap.set(sc.flyerType.name, sc.flyersCount)
            }
        } catch (error) {
            console.log(sc, error)
        }
    })
    const totalGivenCountText = Array.from(fcountMap.entries()).map(([name, count]) => `${name}: ${count.toLocaleString('fr-FR')}`).join(' | ') + ` | Всего: ${totalSpreadCount}`
    
    
    const [isPopup, setPopup] = useState(false);
    const AddSpreadFlyersPopup = () => {

        const thisCityPromoters = [{_id: 'null', fullname: 'Без прома'}, ...promoters.filter(p => p.city._id === city)];
        const [prom, setProm] = useState(thisCityPromoters[0]._id)
        const [routeId, setRouteId] = useState(0);
        
        const [selectedFlyers, selectFlyers] = useState([]);
        const selectFlyerHandler = (id) => {
            if (selectedFlyers.some(f => f === id)) {
                selectFlyers(prev => prev.filter(f => f !== id));
                return;
            }
            selectFlyers(prev => [...prev, id]);
        }

        const [spreadDate, setSpreadDate] = useState(new Date().toDateString())
        const [count, setCount] = useState(0);
        const [isReqSended, setReqSended] = useState(false);

        const addSpreadFlyersHandler = () => {
            const body = {
                city,
                routeId: routeId ? routes.find(r => r.city._id === city && r.id === routeId)?._id : null,
                promoterId: prom === 'null' ? null : prom,
                flyerIds: selectedFlyers,
                count,
                date: +new Date(spreadDate),
                
            }
            setReqSended(true)
            dispatch(addSpreadFlyers({flyersData: body, closeFn: () => setPopup(false), enableBtn: () => setReqSended(false)}));
        }
    
        return <Popup closeFn={() => setPopup(false)} title='Добавить листовки' button={<Button disabled={isReqSended} text='Добавить' onClick={addSpreadFlyersHandler} />}>
            <div className="add-spread-flyers-popup">
            <div style={{display: 'flex', justifyContent: 'space-around'}}>
                <Select label="Пром" enums={[...thisCityPromoters].sort((a,b) => a.fullname.localeCompare(b.fullname))} value={prom} changeHandler={(e) => setProm(e.target.value)}/>
                <Input type='number' min={0} label='№ маршрута (0 если нет)' value={routeId} changeHandler={(e) => setRouteId(+e.target.value)} />
            </div>
                <div className='button-list' style={{marginTop: '16px', display: 'flex', columnGap: '16px', justifyContent: 'center'}}>
                    {flyerTypes.map(f => 
                        <Button key={f._id}
                        classNames={`${selectedFlyers.some(f2 => f2 === f._id)?'active':''}`}
                        text={f.name}
                        onClick={() => selectFlyerHandler(f._id)} />
                    )}
                </div>
                <div style={{display: 'flex', justifyContent: 'space-around', marginTop: '16px'}}>
                    <Input type='date' label='Когда разнесено' value={spreadDate} changeHandler={(e) => setSpreadDate(e.target.value)}/>
                    <Input type="number" min={0} label='Сколько разнесено' value={count} changeHandler={(e) => setCount(+e.target.value)}/>
                </div>
            </div>
        </Popup>
    }

    const isLightTheme = useSelector(state => state.app.isLightTheme);
    const [sort, setSort] = useState({type: 'date', isASC: false});
    const setSortHandler = (type) => {
        if (sort.type === type) {
            setSort(prev => ({...prev, isASC: !sort.isASC}));
            return;
        }
        setSort({type, isASC: false})
    }
    const tableHeaders = [{type:"date", label: 'Дата'}, {type:"promoter", label: 'Пром'}, {type:"flyer", label: 'Листовка'}, {type:"route", label: 'Маршрут'}, {type:"count", label: 'Раскидано'}]

    const TableTitle = ({type, label}) => {
        const isSortByThis = sort.type === type;
        const isASC = sort.isASC;
        return (
            <div onClick={() => setSortHandler(type)} style={{cursor: 'pointer', userSelect: 'none'}}>
                {label}
                {isSortByThis && <img src={isLightTheme ? sortImg : sortDarkImg} style={{transform: `rotate(${isASC?180:0}deg)`}} alt="" />}
            </div>
        )
    }
    const createSortFn = ({type, isASC}) => {
        switch (type) {
            case 'date': return (a, b) => isASC ? b.date - a.date : a.date - b.date;
            case 'promoter': return (a, b) => isASC ? b.promoter.fullname.localeCompare(a.promoter.fullname) : a.promoter.fullname.localeCompare(b.promoter.fullname);
            case 'flyer': return (a, b) => isASC ? b.flyerType._id.localeCompare(a.flyerType._id) : a.flyerType._id.localeCompare(b.flyerType._id);
            case 'route': return (a, b) => isASC ? b.route.id - a.route.id : a.route.id - b.route.id;
            case 'count': return (a, b) => isASC ? b.flyersCount - a.flyersCount : a.flyersCount - b.flyersCount;
            default: return () => true;
        }
    }
    
    const sortedList = [...displayedList].sort(createSortFn(sort));

    const spreadFlyerItems = sortedList.map((l, i) => 
        <ResultItem key={`${l.promoter._id}_${l.route.id}_${l.date}_${l.flyerType._id}_${i}`} groupType={groupType} city={l.city}
        promoter={l.promoter} flyerType={l.flyerType} flyersCount={l.flyersCount} route={l.route} date={l.date} timestamp={l.timestamp}
    />);

    return (
        <div className="spread-flyers">
            <div className="container">
                {isPopup && <AddSpreadFlyersPopup />}
                <div className="spread-flyers__manager">
                    <div className="spread-flyers__select-city">
                        <Select
                            enums={cities}
                            value={city}
                            changeHandler={(e) => setCityHandler(e.target.value)}
                        />
                    </div>
                    <div className="spread-flyers__select-promoter">
                        <SearchSelect
                            options={thisCityPromoters.map(e => ({value: e._id, label: e.fullname}))}
                            value={selectedProm}
                            changeHandler={(e) => selectProm(e.value)}
                        />
                    </div>
                    {/* <div className="spread-flyers__select-promoter">
                        <Select
                            enums={thisCityPromoters}
                            value={selectedProm}
                            changeHandler={(e) => selectProm(e.target.value)}
                        />
                    </div> */}
                    <div className="spread-flyers__select-group">
                        <Select
                            enums={groupFilter}
                            value={groupType}
                            changeHandler={(e) => setGroupType(e.target.value)}
                        />
                    </div>
                    <div className="spread-flyers__select-interval">
                        <span>с</span>
                        <Input style={{margin: '0 6px'}} type='date' value={minDay} changeHandler={(e) => setMinDay(e.target.value)}/>
                        <span>по</span>
                        <Input style={{margin: '0 6px'}} type='date' value={maxDay} changeHandler={(e) => setMaxDay(e.target.value)}/>
                    </div>
                    <div className="spread-flyers__add-item">
                        <Button text='Добавить листовки' onClick={() => setPopup(true)}/>
                    </div>
                </div>
                <div className="spread-flyers__manager">
                    <div className="spread-flyers__total-flyers" style={{marginTop: '8px'}}>
                        <p>Разнесено: {totalGivenCountText}</p>
                    </div>
                </div>
                <div className="spread-flyers__body">
                    <div className="spread-flyers__table">
                        <div className="spread-flyers__titles">
                            {tableHeaders.map(th => <TableTitle key={th.type} type={th.type} label={th.label}/>)}
                        </div>
                        {spreadFlyerItems}
                    </div>
                </div>
                <div className="spread-flyers__footer">
                    <AdvertMonthSwitcher months={months} />
                </div>
            </div>
        </div>
    );
};
